import React, { useContext, useState, useEffect } from 'react'
import { Row, Col, Table, Button } from 'react-bootstrap'
import { VendorContext } from '../../../contexts/vendorContext'
import { useHistory } from 'react-router'
import OrderRepo from '../../../repos/orderRepo'
import { PaginationLimit, OrderItemStatus } from '../../../config/enums'
import { Order } from '../../../models/Order'
import { toast } from 'react-toastify'
import { OrderRow, ordersToOrderRows } from '../../../utility/orderHelper'
import OrderFilters from './orderFilters'
import PaginationTableFooter from '../common/paginationTableFooter'

interface PagingCursor {
  before: Order | undefined
  after: Order | undefined
}

interface Filter {
  status?: OrderItemStatus
  customer?: string
  orderNumber?: string
}

const Orders: React.FC = () => {
  const profile = useContext(VendorContext)!.profile
  const history = useHistory()
  const [orderRepo] = useState(() => new OrderRepo(profile!.vendorId))
  const [pageSize, setPageSize] = useState(PaginationLimit.tables)
  const [orders, setOrders] = useState<Order[]>([])
  const [orderRows, setOrderRows] = useState<OrderRow[]>([])
  const [pagingCursor, setPagingCursor] = useState<PagingCursor>(() => {
    return { before: undefined, after: undefined }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [filters, setFilters] = useState<Filter>({})

  useEffect(() => {
    setIsLoading(true)

    orderRepo
      .fetchOrders(
        pageSize,
        pagingCursor.after,
        pagingCursor.before,
        filters.status,
        filters.orderNumber,
        filters.customer
      )
      .then(pagedOrders => {
        setOrders(pagedOrders)
        setOrderRows(ordersToOrderRows(pagedOrders, profile!.vendorId))

        if (
          pagedOrders.length === 0 &&
          (pagingCursor.after || pagingCursor.before)
        ) {
          // Reset the cursor if went back or prev page too far
          setPagingCursor({
            before: undefined,
            after: undefined
          })
        }

        setIsLoading(false)
      })
      .catch(err => {
        console.error(err)
        toast.error(err, { autoClose: false })
        setIsLoading(false)
      })
  }, [orderRepo, pageSize, pagingCursor, filters, profile])

  const prevPage = () => {
    setPagingCursor({
      after: undefined,
      before: orders[0]
    })
  }

  const nextPage = () => {
    setPagingCursor({
      after: orders[orders.length - 1],
      before: undefined
    })
  }

  return (
    <>
      <Row>
        <Col className="pb-3">
          <h1 className="largeTitle">Order management</h1>
        </Col>
      </Row>

      <OrderFilters
        onFiltersChanged={newFilters => {
          setFilters(newFilters)
          setPagingCursor({
            after: undefined,
            before: undefined
          })
        }}
        onFiltersCleared={() => {
          setFilters({})
          setPagingCursor({
            after: undefined,
            before: undefined
          })
        }}
        isLoading={isLoading}
      />

      <Row>
        <Col className="pt-4">
          <h3>Orders</h3>
        </Col>
      </Row>

      <Table hover className="styledTable">
        <thead>
          <tr>
            <th>Order #</th>
            <th>Customer</th>
            <th>Country</th>
            <th>Amount</th>
            <th>Order created</th>
            <th>Delivery status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {orderRows.length > 0 ? (
            <>
              {orderRows.map(orderRow => {
                return (
                  <tr key={orderRow.id} data-testid="orderRow">
                    <td>{orderRow.orderNumber}</td>
                    <td>{orderRow.customer}</td>
                    <td>{orderRow.country}</td>
                    <td>{orderRow.amount}</td>
                    <td>{orderRow.created}</td>
                    <td>{orderRow.status}</td>
                    <td>
                      <Button
                        size="sm"
                        onClick={() => {
                          history.push(`/vendor/orders/${orderRow.id}`)
                        }}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </>
          ) : (
            <tr>
              <td colSpan={8} className="text-center">
                {isLoading
                  ? 'Loading...'
                  : 'No orders matching your filter criteria'}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {filters.status === undefined && (
        <PaginationTableFooter
          onPageSizeChanged={setPageSize}
          onPrevClicked={prevPage}
          onNextClicked={nextPage}
          isLoading={isLoading}
        />
      )}
    </>
  )
}

export default Orders
